/**
 * Norms Calculation 1 - Mean: color-coding logic shared across table cells, charts and exports.
 * The backend only resolves NormLowerBound (50p) / NormUpperBound (80p) / NormStatus (Applicable /
 * NotApplicable) via CalculateNorms in CrosstabOutputBusinessManager.cs - the actual Blue/Green/Red
 * decision is a pure function of the mean value against those bounds, so it lives here instead of
 * being duplicated per consumer.
 */

export type NormColor = 'blue' | 'green' | 'red' | 'grey';

export const NORM_COLOR_HEX: Record<NormColor, string> = {
  // blue: '#1E88E5',
  // green: '#2E7D32',
  // red: '#C62828',
  blue: '#0070C0',
  green: '#00B050',
  red: '#C00000',
   grey: '#BFBFBF',
};

export const NORM_COLOR_LABEL: Record<NormColor, string> = {
  blue: 'Above country norm',
  green: 'At par with country norm',
  red: 'Below country norm',
  grey: '',
};

/**
 * CSS classes (defined in src/styles.css) for each norm color. Use this - not NORM_COLOR_HEX inline
 * styles - for anything rendered through an [innerHTML] binding: Angular's HTML sanitizer strips the
 * "style" attribute entirely (it isn't in the sanitizer's allowed-attributes list), so an inline
 * `style="color: ..."` silently loses its color there. sigLower/sigHigher elsewhere in this codebase
 * are classes for the same reason.
 */
export const NORM_COLOR_CLASS: Record<NormColor, string> = {
  blue: 'norm-blue',
  green: 'norm-green',
  red: 'norm-red',
  grey: 'norm-grey',
};

/**
 * Static bounds for coloring the Norms change value (vs Previous Year), as opposed to
 * NormLowerBound/NormUpperBound which are the uploaded 50p/80p percentile bounds for the raw mean
 * value itself. A change delta (e.g. +2.3) isn't on the same scale as a percentile band, so it's
 * colored against these fixed thresholds instead.
 */
export const NORM_CHANGE_LOWER_BOUND = 1.1;
export const NORM_CHANGE_UPPER_BOUND = 2.2;

export function getDisplayedMean(
  value: number | null | undefined,
  decimalPlaces = 1
): number | null {
  if (value == null || isNaN(Number(value))) {
    return null;
  }
  return Number(Number(value).toFixed(decimalPlaces));
}

/**
 * Returns the norm color for a mean value against its resolved 50p/80p bounds, or null when Norms
 * doesn't apply (missing value/bounds, or NormStatus came back NotApplicable from the backend).
 * >=80p (upperBound) -> blue, <=50p (lowerBound) -> red, otherwise -> green.
 */
export function getNormColor(
  value: number | null | undefined,
  lowerBound: number | null | undefined,
  upperBound: number | null | undefined
): NormColor | null {
  if (value == null || lowerBound == null || upperBound == null) {
    return null;
  }

  if (value >= upperBound) {
    return 'blue';
  }

  if (value < lowerBound) {
    return 'red';
  }

  return 'green';
}

export function getNormColorHex(
  value: number | null | undefined,
  lowerBound: number | null | undefined,
  upperBound: number | null | undefined
): string | null {
  const color = getNormColor(value, lowerBound, upperBound);
  return color ? NORM_COLOR_HEX[color] : null;
}

export function isRecognitionNormEntry(entry: any): boolean {
  const calculationType = String(entry?.CalculationType || '').toLowerCase();
  return entry?.NormMedianValue != null
    || (calculationType !== '' && calculationType !== 'mean');
}

export function resolveEntryNormColorHex(
  entry: any,
  decimalPlaces: number,
  recognitionSig?: { lower: number | null | undefined; upper: number | null | undefined }
): string | null {
  if (entry?.NormStatus !== 'Applicable') {
    return null;
  }
  if (isRecognitionNormEntry(entry)) {
    const sig = entry.Significance_Value != null
      ? parseFloat(entry.Significance_Value)
      : (entry.Significance_Answer != null ? parseFloat(entry.Significance_Answer) : null);
    const change = entry.changeVsPY ?? entry.changeVsPY_Answer ?? entry.changeVsPY_Total;
    return getRecognitionNormColorHex(
      sig,
      recognitionSig?.lower,
      recognitionSig?.upper,
      change
    );
  }
  return getNormColorHex(
    getDisplayedMean(entry.data, decimalPlaces),
    entry.NormLowerBound,
    entry.NormUpperBound
  );
}

/** Mean only: color the value against 50p/80p. Recognition colors the change, not the value. */
export function resolveValueNormColorHex(
  entry: any,
  decimalPlaces: number,
  recognitionSig?: { lower: number | null | undefined; upper: number | null | undefined }
): string | null {
  if (isRecognitionNormEntry(entry)) {
    return null;
  }
  return resolveEntryNormColorHex(entry, decimalPlaces, recognitionSig);
}

/** Recognition/median only: color the change (z-score), never the raw value. */
export function resolveChangeNormColorHex(
  entry: any,
  decimalPlaces: number,
  recognitionSig?: { lower: number | null | undefined; upper: number | null | undefined }
): string | null {
  if (!isRecognitionNormEntry(entry)) {
    return null;
  }
  return resolveEntryNormColorHex(entry, decimalPlaces, recognitionSig);
}

/** CSS class equivalent of getNormColorHex - use this for [innerHTML]-rendered content. */
export function getNormColorClass(
  value: number | null | undefined,
  lowerBound: number | null | undefined,
  upperBound: number | null | undefined
): string | null {
  const color = getNormColor(value, lowerBound, upperBound);
  return color ? NORM_COLOR_CLASS[color] : null;
}

export function getMedianNormColor(
  zScore: number | null | undefined,
  sigLowerLimit: number | null | undefined,
  sigUpperLimit: number | null | undefined
): NormColor | null {
  if (zScore == null || isNaN(zScore) || sigLowerLimit == null || sigUpperLimit == null) {
    return null;
  }

  if (zScore < sigLowerLimit) {
    return 'blue';
  }

  if (zScore > sigUpperLimit) {
    return 'red';
  }

  return 'green';
}

export function getMedianNormColorHex(
  zScore: number | null | undefined,
  sigLowerLimit: number | null | undefined,
  sigUpperLimit: number | null | undefined
): string | null {
  const color = getMedianNormColor(zScore, sigLowerLimit, sigUpperLimit);
  return color ? NORM_COLOR_HEX[color] : null;
}

export function isNormChangeMissing(change: unknown): boolean {
  if (change == null) {
    return true;
  }
  if (typeof change === 'number') {
    return isNaN(change);
  }
  const text = String(change).trim().toUpperCase();
  return text === '' || text === 'NA' || text === '(NA)';
}

export function getRecognitionNormColor(
  zScore: number | null | undefined,
  sigLowerLimit: number | null | undefined,
  sigUpperLimit: number | null | undefined,
  changeValue?: unknown
): NormColor {
  const color = getMedianNormColor(zScore, sigLowerLimit, sigUpperLimit);
  if (color === 'blue' || color === 'red') {
    return color;
  }
  return isNormChangeMissing(changeValue) ? 'grey' : 'green';
}

export function getRecognitionNormColorHex(
  zScore: number | null | undefined,
  sigLowerLimit: number | null | undefined,
  sigUpperLimit: number | null | undefined,
  changeValue?: unknown
): string {
  return NORM_COLOR_HEX[getRecognitionNormColor(zScore, sigLowerLimit, sigUpperLimit, changeValue)];
}

export function getRecognitionNormColorClass(
  zScore: number | null | undefined,
  sigLowerLimit: number | null | undefined,
  sigUpperLimit: number | null | undefined,
  changeValue?: unknown
): string {
  return NORM_COLOR_CLASS[getRecognitionNormColor(zScore, sigLowerLimit, sigUpperLimit, changeValue)];
}
